/**
 * ReportCalendarPage - Calendario de cheques post-fechados
 * Muestra calendario mensual con cheques próximos a vencer
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import reporteService from '../services/reporteService';

const ReportCalendarPage = () => {
  const { showError } = useNotification();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    loadCalendar();
  }, [currentDate]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await reporteService.getPostDatedCalendar(month, year);
      setReportData(response);
    } catch (error) {
      console.error('Error al cargar calendario:', error);
      showError('Error al cargar el calendario de cheques post-fechados');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  // Agrupar cheques por fecha
  const chequesPorFecha = {};
  reportData?.cheques?.forEach((cheque) => {
    const fecha = cheque.fecha_pago || cheque.fechaCobro;
    if (!chequesPorFecha[fecha]) {
      chequesPorFecha[fecha] = [];
    }
    chequesPorFecha[fecha].push(cheque);
  });

  // Obtener fechas ordenadas
  const fechasOrdenadas = Object.keys(chequesPorFecha).sort();

  // Calcular días para vencimiento
  const getDaysUntilDue = (fechaPago) => {
    const today = new Date();
    const paymentDate = new Date(fechaPago);
    const diffTime = paymentDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Obtener color según días para vencimiento
  const getAlertColor = (fechaPago) => {
    const days = getDaysUntilDue(fechaPago);
    if (days < 0) return 'error';
    if (days <= 3) return 'warning';
    if (days <= 7) return 'info';
    return 'default';
  };

  if (loading && !reportData) {
    return <LoadingSpinner message="Cargando calendario..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Calendario de Cheques Post-fechados"
        subtitle="Calendario mensual con cheques próximos a vencer"
        breadcrumbs={[
          { label: 'Inicio', to: '/dashboard' },
          { label: 'Reportes' },
          { label: 'Calendario' },
        ]}
      />

      {/* Selector de mes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Button
              startIcon={<ChevronLeft />}
              onClick={handlePrevMonth}
              variant="outlined"
              disabled={loading}
            >
              Mes Anterior
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="h5">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              endIcon={<ChevronRight />}
              onClick={handleNextMonth}
              variant="outlined"
              disabled={loading}
            >
              Mes Siguiente
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cheques del Mes
              </Typography>
              <Typography variant="h4">{reportData?.cheques?.length || 0}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total: {formatCurrency(reportData?.totales?.monto_total)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Próximos a Vencer (7 días)
              </Typography>
              <Typography variant="h4" color="warning.main">
                {reportData?.cheques?.filter(ch => {
                  const days = getDaysUntilDue(ch.fecha_pago || ch.fechaCobro);
                  return days >= 0 && days <= 7;
                }).length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatCurrency(
                  reportData?.cheques
                    ?.filter(ch => {
                      const days = getDaysUntilDue(ch.fecha_pago || ch.fechaCobro);
                      return days >= 0 && days <= 7;
                    })
                    ?.reduce((sum, ch) => {
                      const monto = parseFloat(ch.monto);
                      return sum + (isNaN(monto) ? 0 : monto);
                    }, 0) || 0
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alertas */}
      {reportData?.cheques?.some(ch => getDaysUntilDue(ch.fecha_pago || ch.fechaCobro) <= 3 && getDaysUntilDue(ch.fecha_pago || ch.fechaCobro) >= 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Hay cheques próximos a vencer en los próximos 3 días. Revise el listado a continuación.
          </Typography>
        </Alert>
      )}

      {/* Listado de cheques por fecha */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cheques Post-fechados del Mes
        </Typography>

        {fechasOrdenadas.length === 0 ? (
          <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
            No hay cheques post-fechados para este mes
          </Typography>
        ) : (
          fechasOrdenadas.map((fecha) => {
            const chequesDia = chequesPorFecha[fecha];
            const daysUntilDue = getDaysUntilDue(fecha);

            return (
              <Box key={fecha} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {formatDate(fecha)}
                  </Typography>
                  <Chip
                    label={
                      daysUntilDue < 0
                        ? `Vencido hace ${Math.abs(daysUntilDue)} día(s)`
                        : daysUntilDue === 0
                        ? 'Vence hoy'
                        : `Vence en ${daysUntilDue} día(s)`
                    }
                    color={getAlertColor(fecha)}
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {chequesDia.length} cheque(s) - {formatCurrency(chequesDia.reduce((sum, ch) => {
                      const monto = parseFloat(ch.monto);
                      return sum + (isNaN(monto) ? 0 : monto);
                    }, 0))}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Banco</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chequesDia.map((cheque) => (
                        <TableRow key={cheque.id}>
                          <TableCell>{cheque.numero_cheque}</TableCell>
                          <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                          <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                          <TableCell align="right">
                            <strong>{formatCurrency(cheque.monto)}</strong>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cheque.estado}
                              size="small"
                              color={cheque.estado === 'pendiente' ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })
        )}
      </Paper>
    </Box>
  );
};

export default ReportCalendarPage;
